import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Labeled, TextInput, cn } from '@tes/ui';
import { api } from '../lib/api';
import { messageOf } from '../lib/errors';
import type { Branding, PlatformSettings } from '../lib/types';

/** A full http(s) link — mirrors the API check so Save can't be pressed on a typo. */
function isVideoLink(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

/** Plays the SAVED link so an admin can confirm the host serves it before signers see it. */
function VideoPreview({ url, vertical }: { url: string; vertical: boolean }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <p className="mt-2 text-xs text-red-600">
        This video couldn’t be loaded. Check the link is public and points straight at an .mp4 file.
      </p>
    );
  }
  return (
    <video
      src={url}
      controls
      muted
      playsInline
      preload="metadata"
      onError={() => setFailed(true)}
      className={cn('mt-2 rounded border border-slate-200 bg-black', vertical ? 'h-48 aspect-[9/16]' : 'w-full max-w-xs aspect-video')}
    />
  );
}

export function TutorialVideoSettings({ branding }: { branding: Branding }) {
  // Seed the inputs once from the saved links; a save re-seeds from what was
  // written (trimmed), and other branding edits leave half-typed links alone.
  const version = `${branding.tutorialDesktopVideoUrl ?? ''}|${branding.tutorialMobileVideoUrl ?? ''}`;
  return <TutorialVideoForm key={version} branding={branding} />;
}

function TutorialVideoForm({ branding }: { branding: Branding }) {
  const qc = useQueryClient();
  const [desktop, setDesktop] = useState(branding.tutorialDesktopVideoUrl ?? '');
  const [mobile, setMobile] = useState(branding.tutorialMobileVideoUrl ?? '');

  const save = useMutation({
    mutationFn: () =>
      api.patchBranding({ tutorialDesktopVideoUrl: desktop.trim(), tutorialMobileVideoUrl: mobile.trim() }),
    onSuccess: (updated: PlatformSettings) => qc.setQueryData(['settings'], updated),
  });

  const invalid = [desktop, mobile].some((v) => v.trim() !== '' && !isVideoLink(v.trim()));
  const unchanged =
    desktop.trim() === (branding.tutorialDesktopVideoUrl ?? '') &&
    mobile.trim() === (branding.tutorialMobileVideoUrl ?? '');
  const enabled = Boolean(branding.tutorialDesktopVideoUrl || branding.tutorialMobileVideoUrl);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">How-to-sign videos</h2>
        {save.isPending && <span className="text-xs text-slate-400">Saving…</span>}
      </div>
      <p className="mb-4 text-xs text-slate-400">
        When set, a “How to sign” video pops up every time a signer opens their link, and a
        Tutorial button lets them watch it again. Paste links to MP4 files hosted elsewhere. Clear
        both to turn it off.{' '}
        <span className={enabled ? 'font-medium text-emerald-600' : 'font-medium text-slate-500'}>
          {enabled ? 'Currently on.' : 'Currently off.'}
        </span>
      </p>
      {save.error && <p className="mb-3 text-sm text-red-600">{messageOf(save.error, 'Save failed')}</p>}

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <Labeled
            label="Desktop video (landscape 16:9)"
            hint="Shown on computers, tablets and phones turned sideways."
          >
            <TextInput
              type="url"
              value={desktop}
              onChange={(e) => setDesktop(e.target.value)}
              placeholder="https://…/how-to-sign-desktop.mp4"
              spellCheck={false}
            />
          </Labeled>
          {branding.tutorialDesktopVideoUrl && (
            <VideoPreview key={branding.tutorialDesktopVideoUrl} url={branding.tutorialDesktopVideoUrl} vertical={false} />
          )}
        </div>
        <div>
          <Labeled
            label="Mobile video (vertical 9:16)"
            hint="Shown on phones held upright. If empty, phones get the desktop video."
          >
            <TextInput
              type="url"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="https://…/how-to-sign-mobile.mp4"
              spellCheck={false}
            />
          </Labeled>
          {branding.tutorialMobileVideoUrl && (
            <VideoPreview key={branding.tutorialMobileVideoUrl} url={branding.tutorialMobileVideoUrl} vertical />
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Button size="sm" onClick={() => save.mutate()} disabled={invalid || unchanged || save.isPending}>
          Save videos
        </Button>
        {invalid && (
          <span className="text-xs text-red-600">Links must be full addresses starting with https://</span>
        )}
      </div>
    </section>
  );
}
